/**
 * Shared analyst-filtered record computation.
 *
 * Given a team's per-game results (BigRoad cells) and an analyst filter
 * ("both" | "charlie" | "dakota"), returns the actual / theoretical W-L
 * plus an explicit skip W/L split using cell.skipped_analysts when present
 * (fallback to derived theo−actual when the backend hasn't populated it).
 *
 * Used by:
 *   - src/components/BigRoad.jsx  (chart-frame badge)
 *   - src/pages/StrategyLab.jsx   (PinnedTile + UniverseRow records)
 *
 * Keeping the math in one place prevents the kind of badge-mismatch bug
 * caught by the iter22 testing agent (header showed 6-1-5 while inner badge
 * showed 3-1-5 because the two callers used different theo definitions).
 *
 * @typedef {Object} Cell
 * @property {"W"|"L"|"T"|string} result
 * @property {string[]} [actual_analysts]
 * @property {string[]} [theoretical_analysts]
 * @property {string[]} [skipped_analysts]
 * @property {string[]} [pick_analysts]  // legacy / unified fallback
 */

const ANALYST_NAME = { charlie: "Charlie", dakota: "Dakota" };

/**
 * Returns the analyst-keep predicate for an analyst-list field on a cell.
 */
function makeMatcher(analystFilter) {
  if (analystFilter === "both") return (arr) => (arr || []).length > 0;
  const want = ANALYST_NAME[analystFilter];
  return (arr) => (arr || []).includes(want);
}

/**
 * Resolve actual / theoretical analyst lists on a cell, honoring the
 * BigRoad mode-fallback convention (some legacy payloads only carry
 * `pick_analysts` and rely on `mode` to decide which bucket it represents).
 */
export function resolvePicks(cell, mode = "diff") {
  if (!cell || typeof cell !== "object") return { actual: [], theo: [], skipped: [] };
  const actual = cell.actual_analysts
    || (mode !== "theoretical" ? cell.pick_analysts : null)
    || [];
  const theo = cell.theoretical_analysts
    || (mode === "theoretical" ? cell.pick_analysts : null)
    || [];
  const skipped = cell.skipped_analysts || [];
  return { actual, theo, skipped };
}

/**
 * Compute the {actualW, actualL, theoW, theoL, skippedW, skippedL, skipped}
 * record for the given results array under the given analyst filter.
 *
 * @param {Cell[]} results
 * @param {"both"|"charlie"|"dakota"} analystFilter
 * @param {"actual"|"theoretical"|"diff"} [mode="diff"]
 */
export function computeAnalystRecord(results, analystFilter, mode = "diff") {
  const arr = Array.isArray(results) ? results : [];
  const match = makeMatcher(analystFilter);
  let actualW = 0, actualL = 0;
  let theoW = 0, theoL = 0;
  let skipW = 0, skipL = 0;
  for (const r of arr) {
    if (!r || typeof r !== "object") continue;
    const isW = r.result === "W";
    const isL = r.result === "L";
    if (!isW && !isL) continue;
    const { actual, theo, skipped } = resolvePicks(r, mode);
    const inActual = match(actual);
    // actual is conceptually a subset of theoretical — count any-actual as theo too.
    const inTheo = match(theo) || inActual;
    const inSkip = match(skipped);
    if (inActual) (isW ? actualW++ : actualL++);
    if (inTheo) (isW ? theoW++ : theoL++);
    if (inSkip) (isW ? skipW++ : skipL++);
  }
  // Dev-only sanity check: actual should be ⊆ theoretical for any analyst.
  if (process.env.NODE_ENV !== "production" && (actualW > theoW || actualL > theoL)) {
    console.warn(
      `[analystRecord] actual > theoretical for filter=${analystFilter}:`,
      { actualW, actualL, theoW, theoL },
      "— backend may be omitting actual analyst names from theoretical_analysts."
    );
  }
  // Prefer backend-supplied explicit skip count; fall back to derived theo − actual gap.
  const hasExplicitSkip = (skipW + skipL) > 0;
  const skippedW = hasExplicitSkip ? skipW : Math.max(0, theoW - actualW);
  const skippedL = hasExplicitSkip ? skipL : Math.max(0, theoL - actualL);
  return {
    actualW, actualL,
    theoW, theoL,
    skippedW, skippedL,
    skipped: skippedW + skippedL,
  };
}

/**
 * Convenience: $250 per hit per unit, matching the comparison-matrix display.
 */
export const dollarsFor = (w, l) => (w || 0) * 250 - (l || 0) * 250;
